import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import { motion } from "framer-motion";
import {
  GraduationCap,
  Building2,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import LogoMarquee from "../../components/ui/LogoMarquee";
import { fadeUp } from "../../utils/animations";
import heroBg from "./herobackground.png";

const HERO_IMAGE_URL = heroBg;

const visitorChoices = [
  {
    type: "student",
    href: "/learn",
    label: "I am a student",
    sub: "Explore learning paths",
    icon: GraduationCap,
    variant: "solid",
  },
  {
    type: "business",
    href: "/business?tab=overview",
    label: "Corporate Training",
    sub: "Talk to our experts",
    icon: Building2,
    variant: "glass",
  },
];

const stats = [
  { value: "40K+", label: "Professionals trained" },
  { value: "180+", label: "Enterprise partners" },
  { value: "60+", label: "Certification tracks" },
];

const accreditations = [
  { name: "NAICU", src: "/logo/NAICU.png" },
  { name: "PMP", src: "/logo/PMP.png" },
  { name: "AAHEA", src: "/logo/AAHEA.png" },
  { name: "GARP", src: "/logo/GARP.jpg" },
  { name: "The Agile Alliance", src: "/logo/AgileAlliance.jpg" },
  { name: "Six Sigma Council", src: "/logo/SixSigmaCouncil.jpg" },
];

export default function Gateway() {
  const navigate = useNavigate();

  return (
    <Box
      component="section"
      sx={{
        minHeight: "100vh",
        bgcolor: "#FAFBFF",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ============ PREMIUM HERO — background image + gradient scrim ============ */}
      <Box
        sx={{
          position: "relative",
          pt: { xs: 14, md: 18 },
          pb: { xs: 12, md: 16 },
          overflow: "hidden",
        }}
      >
        {/* Background photograph */}
        <Box
  aria-hidden
  sx={{
    position: "absolute",
    inset: 0,
    backgroundImage: `url(${HERO_IMAGE_URL})`,
    backgroundSize: "cover",
    backgroundPosition: { xs: "right center", md: "center 30%" },
    transform: { xs: "none", md: "scale(1.04)" },
  }}
/>

        {/* Directional gradient scrim */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(100deg, rgba(6,12,32,0.94) 0%, rgba(8,16,42,0.86) 30%, rgba(11,21,48,0.55) 56%, rgba(11,21,48,0.28) 74%, rgba(11,21,48,0.12) 100%)",
          }}
        />

        {/* Deep vertical fade into the page background at the bottom edge */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            height: { xs: 160, md: 220 },
            background: "linear-gradient(180deg, rgba(250,251,255,0) 0%, #FAFBFF 100%)",
          }}
        />

        {/* Fine blueprint grid, subtle, only over the scrim side */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "46px 46px",
            maskImage:
              "linear-gradient(100deg, black 0%, black 35%, transparent 65%)",
            WebkitMaskImage:
              "linear-gradient(100deg, black 0%, black 35%, transparent 65%)",
          }}
        />

        {/* Ambient blue glow for extra depth */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: -140,
            left: "8%",
            width: 700,
            height: 500,
            background:
              "radial-gradient(ellipse 50% 50% at 50% 50%, rgba(37,99,235,0.32), transparent 70%)",
            pointerEvents: "none",
            filter: "blur(10px)",
          }}
        />

        <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
          <motion.div {...fadeUp(0)}>
            <Box sx={{ maxWidth: 760, mb: { xs: 5, md: 6 } }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.75,
                  py: 0.6,
                  borderRadius: 999,
                  border: "1px solid rgba(255,255,255,0.22)",
                  bgcolor: "rgba(255,255,255,0.06)",
                  backdropFilter: "blur(8px)",
                  mb: 2.75,
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#5B9BFF" }} />
                <Typography
                  sx={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    color: "#BFD4FF",
                    textTransform: "uppercase",
                  }}
                >
                  Who are you?
                </Typography>
              </Box>

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "2.1rem", sm: "2.7rem", md: "3.4rem" },
                  lineHeight: 1.08,
                  letterSpacing: "-0.025em",
                  color: "#FFFFFF",
                  textShadow: "0 2px 24px rgba(0,0,0,0.35)",
                }}
              >
                Launch your IT career.{" "}
                <Box
                  component="span"
                  sx={{
                    background: "linear-gradient(90deg, #7CB2FF 0%, #A9C9FF 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  Transform your business.
                </Box>
              </Typography>

              <Typography
                sx={{
                  mt: 2.75,
                  fontSize: { xs: 15, md: 17.5 },
                  fontWeight: 400,
                  lineHeight: 1.75,
                  color: "rgba(226,232,255,0.86)",
                  maxWidth: 560,
                }}
              >
                Industry-leading training, certifications, cloud programs, and enterprise
                technology solutions from NETLABS ITS.
              </Typography>
            </Box>
          </motion.div>

          {/* Stat strip — glass panel floating on the image */}
          <motion.div {...fadeUp(0.08)}>
            <Box
              sx={{
                display: "inline-flex",
                flexWrap: "wrap",
                gap: { xs: 3, md: 5 },
                rowGap: 2,
                px: { xs: 2.5, md: 3.5 },
                py: { xs: 2, md: 2.25 },
                borderRadius: "16px",
                border: "1px solid rgba(255,255,255,0.16)",
                bgcolor: "rgba(255,255,255,0.06)",
                backdropFilter: "blur(14px)",
              }}
            >
              {stats.map((s) => (
                <Box key={s.label} sx={{ textAlign: "left" }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "1.4rem", md: "1.7rem" },
                      fontWeight: 800,
                      color: "#FFFFFF",
                      letterSpacing: "-0.02em",
                      lineHeight: 1,
                    }}
                  >
                    {s.value}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12.5, color: "rgba(226,232,255,0.75)", mt: 0.5, fontWeight: 600 }}
                  >
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </motion.div>

          {/* Choice buttons — compact, sits below the stats strip, still inside the hero */}
          <motion.div {...fadeUp(0.12)}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ mt: 2.5, maxWidth: 480 }}
            >
              {visitorChoices.map((choice) => {
                const Icon = choice.icon;
                const isSolid = choice.variant === "solid";
                return (
                  <Box
                    key={choice.type}
                    component="button"
                    onClick={() => navigate(choice.href)}
                    sx={{
                      all: "unset",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      flex: 1,
                      px: 2,
                      py: 1.1,
                      borderRadius: "999px",
                      border: isSolid ? "1px solid transparent" : "1px solid rgba(255,255,255,0.28)",
                      background: isSolid
                        ? "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)"
                        : "rgba(255,255,255,0.08)",
                      boxShadow: isSolid ? "0 10px 22px -10px rgba(37,99,235,0.6)" : "none",
                      backdropFilter: isSolid ? "none" : "blur(10px)",
                      transition: "transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: isSolid
                          ? "0 14px 28px -10px rgba(37,99,235,0.75)"
                          : "0 8px 18px -10px rgba(0,0,0,0.4)",
                        background: isSolid
                          ? "linear-gradient(135deg, #2E6EF0 0%, #2050E0 100%)"
                          : "rgba(255,255,255,0.14)",
                      },
                      "&:hover .choice-arrow": { transform: "translateX(3px)" },
                    }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "50%",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: isSolid ? "rgba(255,255,255,0.18)" : "rgba(255,255,255,0.1)",
                      }}
                    >
                      <Icon size={15} color="#FFFFFF" strokeWidth={2} />
                    </Box>

                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#FFFFFF",
                        lineHeight: 1.2,
                        flex: 1,
                      }}
                    >
                      {choice.label}
                    </Typography>

                    <ArrowRight
                      size={14}
                      color="#FFFFFF"
                      className="choice-arrow"
                      style={{ transition: "transform 0.2s ease", flexShrink: 0, opacity: 0.85 }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </motion.div>
        </Box>
      </Box>

      {/* Partner logos */}
      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
        <motion.div {...fadeUp(0.2)}>
          <Box sx={{ textAlign: "center", pb: 8 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{
    mb: 2.5,
    width: "100%",
    justifyContent: "center",
  }}
            >
              <Sparkles size={13} color="#94A3B8" />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  color: "#94A3B8",
                  textTransform: "uppercase",
                }}
              >
                Trusted training partners
              </Typography>
            </Stack>
            <LogoMarquee />
          </Box>
        </motion.div>
      </Box>

      {/* ============ Accreditations — logo cards, grayscale-to-color on hover ============ */}
      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
        <motion.div {...fadeUp(0.24)}>
          <Box sx={{ textAlign: "center", pb: 10 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
               sx={{
    mb: 2.5,
    width: "100%",
    justifyContent: "center",
  }}>
              <Sparkles size={13} color="#94A3B8" />
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  color: "#94A3B8",
                  textTransform: "uppercase",
                }}
              >
                Accreditations
              </Typography>
            </Stack>

            <Typography
              sx={{
                fontSize: { xs: 12.5, md: 13.5 },
                color: "#94A3B8",
                mb: 4,
                fontWeight: 500,
              }}
            >
              Recognized and endorsed by leading global bodies
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(6, 1fr)",
                },
                gap: { xs: 1.5, md: 2 },
              }}
            >
              {accreditations.map((item) => (
                <Box
                  key={item.name}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.25,
                    height: { xs: 96, md: 108 },
                    px: 2,
                    borderRadius: "14px",
                    bgcolor: "#FFFFFF",
                    border: "1px solid rgba(15,23,42,0.08)",
                    boxShadow: "0 2px 8px rgba(15,23,42,0.04)",
                   transition: "transform 0.24s ease, box-shadow 0.24s ease, border-color 0.24s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 14px 28px -14px rgba(37,99,235,0.35)",
                      borderColor: "rgba(37,99,235,0.28)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={item.src}
                    alt={item.name}
                    sx={{
                      height: { xs: 32, md: 36 },
                      width: { xs: 32, md: 36 },
                      objectFit: "contain",
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: 10.5, md: 11.5 },
                      fontWeight: 600,
                      color: "#64748B",
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    {item.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}